import {
  confirmIndustrialCostImportedManufacturingOrdersImport,
  previewIndustrialCostImportedManufacturingOrdersImport,
} from '../services/industrialCostService';
import type {
  ImportedManufacturingOrderImportConfirmResponse,
  ImportedManufacturingOrderImportCorrection,
  ImportedManufacturingOrderImportPreviewResponse,
} from '../types/industrialCost';

export type ImportedManufacturingOrdersImportFlowStatus =
  | 'idle'
  | 'loading'
  | 'needs_mapping'
  | 'ready'
  | 'confirming'
  | 'imported'
  | 'imported_with_issues'
  | 'error';

export type ImportedManufacturingOrdersImportFlowIssue = {
  kind:
    | 'missing_required_fields'
    | 'canonical_blocking_issues'
    | 'warnings';
  severity: 'blocking' | 'non_blocking';
  count: number;
  message: string;
};

export type ImportedManufacturingOrdersImportFlowSummary = {
  canContinue: boolean;
  blockingIssues: ImportedManufacturingOrdersImportFlowIssue[];
  nonBlockingIssues: ImportedManufacturingOrdersImportFlowIssue[];
  allIssues: ImportedManufacturingOrdersImportFlowIssue[];
  missingRequiredFieldsCount: number;
  canonicalBlockingIssuesCount: number;
  warningsCount: number;
};

export type ImportedManufacturingOrdersImportFlowState = {
  file: File | null;
  preview: ImportedManufacturingOrderImportPreviewResponse | null;
  confirmResult: ImportedManufacturingOrderImportConfirmResponse | null;
  mapping: Record<string, string | null>;
  productResolution: Record<string, string>;
  corrections: ImportedManufacturingOrderImportCorrection[];
  normalization: {
    sheet_name?: string | null;
    header_row_index?: number | null;
  } | null;
  status: ImportedManufacturingOrdersImportFlowStatus;
  error: string | null;
  resultMessage: string | null;
};

export function createImportedManufacturingOrdersImportFlowState(
  file: File | null = null,
): ImportedManufacturingOrdersImportFlowState {
  return {
    file,
    preview: null,
    confirmResult: null,
    mapping: {},
    productResolution: {},
    corrections: [],
    normalization: null,
    status: 'idle',
    error: null,
    resultMessage: null,
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeError.response?.data?.detail) {
      return maybeError.response.data.detail;
    }

    if (maybeError.message) {
      return maybeError.message;
    }
  }

  return fallback;
}

export function deriveImportedManufacturingOrdersImportFlowStatus(
  preview: ImportedManufacturingOrderImportPreviewResponse | null,
): ImportedManufacturingOrdersImportFlowStatus {
  if (!preview) {
    return 'idle';
  }

  if ((preview.missing_required_fields?.length ?? 0) > 0) {
    return 'needs_mapping';
  }

  return 'ready';
}

export function getImportedManufacturingOrdersUnresolvedProductRefs(
  state: ImportedManufacturingOrdersImportFlowState,
) {
  if (!state.preview) {
    return [];
  }

  return state.preview.detected_product_refs.filter((item) => {
    const resolvedProductId =
      state.productResolution[item.source_value] ??
      item.resolved_product_id;

    return !resolvedProductId;
  });
}

export async function runImportedManufacturingOrdersImportPreview(
  state: ImportedManufacturingOrdersImportFlowState,
  overrides?: {
    file?: File | null;
    mapping?: Record<string, string | null>;
    productResolution?: Record<string, string>;
    corrections?: ImportedManufacturingOrderImportCorrection[];
    normalization?: {
      sheet_name?: string | null;
      header_row_index?: number | null;
    } | null;
  },
): Promise<ImportedManufacturingOrdersImportFlowState> {
  const file = overrides?.file ?? state.file;
  const mapping = overrides?.mapping ?? state.mapping;
  const incomingResolution =
    overrides?.productResolution ?? state.productResolution;
  const corrections =
    overrides?.corrections ?? state.corrections;
  const normalization =
    overrides?.normalization ?? state.normalization;

  if (!file) {
    return {
      ...state,
      file: null,
      preview: null,
      confirmResult: null,
      productResolution: incomingResolution,
      corrections,
      normalization: null,
      status: 'idle',
      error: null,
      resultMessage: null,
    };
  }

  try {
    const preview =
      await previewIndustrialCostImportedManufacturingOrdersImport(
        file,
        mapping,
        incomingResolution,
        normalization ?? undefined,
        corrections,
      );

    const nextResolution: Record<string, string> = {
      ...incomingResolution,
    };

    preview.detected_product_refs.forEach((item) => {
      if (
        !nextResolution[item.source_value] &&
        item.resolved_product_id
      ) {
        nextResolution[item.source_value] =
          item.resolved_product_id;
      }
    });

    return {
      ...state,
      file,
      preview,
      confirmResult: null,
      mapping: preview.auto_mapping ?? mapping,
      productResolution: nextResolution,
      corrections,
      normalization,
      status:
        deriveImportedManufacturingOrdersImportFlowStatus(
          preview,
        ),
      error: null,
      resultMessage: null,
    };
  } catch (error) {
    return {
      ...state,
      file,
      preview: null,
      confirmResult: null,
      productResolution: incomingResolution,
      corrections,
      normalization,
      status: 'error',
      error: getErrorMessage(
        error,
        'No se pudo leer el archivo de pedidos pendientes de fabricar.',
      ),
      resultMessage: null,
    };
  }
}

export async function applyImportedManufacturingOrdersImportMapping(
  state: ImportedManufacturingOrdersImportFlowState,
  field: string,
  column: string,
): Promise<ImportedManufacturingOrdersImportFlowState> {
  const nextMapping = {
    ...state.mapping,
    [field]: column || null,
  };

  return runImportedManufacturingOrdersImportPreview(state, {
    mapping: nextMapping,
  });
}

export async function applyImportedManufacturingOrdersProductResolution(
  state: ImportedManufacturingOrdersImportFlowState,
  sourceValue: string,
  productId: string,
): Promise<ImportedManufacturingOrdersImportFlowState> {
  const nextResolution = {
    ...state.productResolution,
    [sourceValue]: productId,
  };

  return runImportedManufacturingOrdersImportPreview(state, {
    productResolution: nextResolution,
  });
}

export async function confirmImportedManufacturingOrdersImport(
  state: ImportedManufacturingOrdersImportFlowState,
): Promise<ImportedManufacturingOrdersImportFlowState> {
  if (!state.file) {
    return {
      ...state,
      status: 'error',
      error: 'No hay archivo seleccionado para importar.',
    };
  }

  try {
    const confirmResult =
      await confirmIndustrialCostImportedManufacturingOrdersImport(
        state.file,
        state.mapping,
        state.productResolution,
        state.normalization ?? undefined,
        state.corrections,
      );

    // IND-004: filas repetidas o descartadas → «Con avisos».
    const duplicates = confirmResult.rows_skipped_duplicate_in_file ?? 0;
    const otherRowErrors = Math.max(confirmResult.row_errors.length - duplicates, 0);
    const issueDetails = [
      duplicates > 0 ? `${duplicates} fila(s) de OF repetidas omitidas` : null,
      otherRowErrors > 0 ? `${otherRowErrors} fila(s) descartadas` : null,
      confirmResult.persistence_errors.length > 0
        ? `${confirmResult.persistence_errors.length} errores al guardar`
        : null,
    ].filter((value): value is string => value !== null);

    return {
      ...state,
      confirmResult,
      status: issueDetails.length > 0 ? 'imported_with_issues' : 'imported',
      error: null,
      resultMessage: `Importación completada. Creadas: ${confirmResult.rows_created}. Actualizadas: ${confirmResult.rows_updated}. Importadas: ${confirmResult.rows_imported}.${issueDetails.length > 0 ? ` Avisos: ${issueDetails.join(' · ')}.` : ''}`,
    };
  } catch (error) {
    return {
      ...state,
      confirmResult: null,
      status: 'error',
      error: getErrorMessage(
        error,
        'No se pudo confirmar la importación de pedidos pendientes de fabricar.',
      ),
      resultMessage: null,
    };
  }
}

export function getImportedManufacturingOrdersImportSummary(
  state: ImportedManufacturingOrdersImportFlowState,
): ImportedManufacturingOrdersImportFlowSummary {
  const preview = state.preview;

  if (!preview) {
    return {
      canContinue: false,
      blockingIssues: [],
      nonBlockingIssues: [],
      allIssues: [],
      missingRequiredFieldsCount: 0,
      canonicalBlockingIssuesCount: 0,
      warningsCount: 0,
    };
  }

  const missingRequiredFieldsCount =
    preview.missing_required_fields.length;

  const canonicalBlockingIssuesCount =
    preview.blocking_issues.length;

  const warningsCount = preview.row_warnings.length;

  const blockingIssues: ImportedManufacturingOrdersImportFlowIssue[] =
    [];
  const nonBlockingIssues: ImportedManufacturingOrdersImportFlowIssue[] =
    [];

  if (missingRequiredFieldsCount > 0) {
    blockingIssues.push({
      kind: 'missing_required_fields',
      severity: 'blocking',
      count: missingRequiredFieldsCount,
      message: `Faltan ${missingRequiredFieldsCount} campo(s) obligatorio(s) por mapear.`,
    });
  }

  if (canonicalBlockingIssuesCount > 0) {
    blockingIssues.push({
      kind: 'canonical_blocking_issues',
      severity: 'blocking',
      count: canonicalBlockingIssuesCount,
      message:
        `Hay ${canonicalBlockingIssuesCount} fila(s) incompatibles ` +
        'con la publicación del snapshot canónico.',
    });
  }

  if (warningsCount > 0) {
    nonBlockingIssues.push({
      kind: 'warnings',
      severity: 'non_blocking',
      count: warningsCount,
      message: `Se han detectado ${warningsCount} advertencia(s) en el archivo.`,
    });
  }

  return {
    canContinue:
      missingRequiredFieldsCount === 0 &&
      canonicalBlockingIssuesCount === 0,
    blockingIssues,
    nonBlockingIssues,
    allIssues: [
      ...blockingIssues,
      ...nonBlockingIssues,
    ],
    missingRequiredFieldsCount,
    canonicalBlockingIssuesCount,
    warningsCount,
  };
}